import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useStudioUser } from './useStudioUser';
import {
  PENDING_PLATFORM_UPGRADE_KEY,
  redirectToBillingSetupWizard,
} from './useBillingSetupRedirect';

/**
 * Hook to poll subscription status in the background
 * Checks every 2 minutes to detect manual cancellations in Stripe
 */
export function useSubscriptionPolling() {
  const { getAuthToken, isSubAccount } = useAuth();
  const { studioUser } = useStudioUser();
  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Don't poll if no studio user
    if (!studioUser?.studio_id) {
      return;
    }
    // Sub-accounts don't manage billing; only main account needs subscription sync/redirect
    if (isSubAccount) {
      return;
    }

    const checkSubscriptionStatus = async () => {
      try {
        const authToken = getAuthToken();
        if (!authToken) return;

        if (sessionStorage.getItem(PENDING_PLATFORM_UPGRADE_KEY) === '1') {
          const reconcileResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/platform-billing/reconcile-free-upgrade`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({}),
            }
          );

          if (reconcileResponse.ok) {
            const reconcileData = await reconcileResponse.json();
            if (reconcileData.upgraded) {
              sessionStorage.removeItem(PENDING_PLATFORM_UPGRADE_KEY);
              redirectToBillingSetupWizard();
              return;
            }
          }
        }

        // Call the sync endpoint to get fresh data from Stripe
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/platform-billing/sync-subscription-status/${studioUser.studio_id}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          
          if (data.reconciled) {
            redirectToBillingSetupWizard();
            return;
          }

          // If subscription became inactive, redirect immediately
          if (data.current_is_active === false && data.previous_is_active === true) {
            console.log('🚨 Subscription became inactive - redirecting to billing');
            navigate('/app/inactive-studio/settings/billing', { replace: true });
            
            // Optionally reload to clear any cached data
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
        // Don't disrupt user experience if polling fails
      }
    };

    // Check immediately on mount
    checkSubscriptionStatus();

    // Then check every 2 minutes (120000ms)
    intervalRef.current = setInterval(checkSubscriptionStatus, 120000);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [studioUser?.studio_id, getAuthToken, navigate]);
}

